// 委派詳情（SPEC #org-tree-ui 第 3 點）：
//   委派方（o22）：受託組織、代號、建立人、建立 / 上次覆核、節點與範圍、期限、歷史、套用的政策；「對方的被指派人」唯讀表（W-02）；最近 30 天經這筆委派的操作 → 稽核；
//                 〔編輯範圍〕〔延長 / 設期限〕〔撤銷委派〕〔仍需要〕——縮小 / 撤銷前先預覽（o23）
//   受託方（o29）：委派方、委派人、建立 / 擴大時間、節點、範圍、期限、歷史、兩步驟驗證要求；「本組織的指派」表（實際生效、不會生效、超出委派範圍、可擴大），
//                 每列〔編輯〕〔撤銷〕（o29b）；頁首〔套用…〕〔指派成員〕〔結束委派〕；表下「<委派方> 看得到這份名單」
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '../../components/Dialog';
import { useConfirm } from '../../components/ConfirmDialog';
import type { MyOrg } from '../../lib/planeApi';
import { DELEGATION_SCOPES, HIGH_RISK_SCOPES, treeApi, type Assignment, type Delegation } from '../../lib/orgTreeApi';
import { actionWords, termText } from '../../lib/orgTreeUi';
import { AssignDialog } from './AssignDialog';
import { PreviewDialog } from './PreviewDialog';
import { StatusTags } from './DelegationsTab';
import { useTreeRefresh } from './hooks';
import { otErr } from './otErr';
import { dateStamp } from '../../lib/timefmt';

type Flow = { kind: 'scope' } | { kind: 'term' } | { kind: 'revoke' } | { kind: 'apply' } | { kind: 'd-revoke'; a: Assignment } | null;

export function DelegationDetail({ org }: { org: MyOrg }) {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['ot', org.org_id, 'dlg-detail', id], queryFn: () => treeApi.delegation(org, id), retry: false });
  const d = q.data;
  const side: 'out' | 'in' | null = d ? (d.from_org_id === org.org_id ? 'out' : 'in') : null;
  const dAs = useQuery({ queryKey: ['ot', org.org_id, 'dlg-assignments', id], queryFn: () => treeApi.dAssignments(org, id), enabled: side === 'in', retry: false });
  const refresh = useTreeRefresh(org);
  const nav = useNavigate();
  const { ask, dialog } = useConfirm();
  const [flow, setFlow] = useState<Flow>(null);
  const [assign, setAssign] = useState<{ existing?: Assignment } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fmt = (s?: string | null) => (s ? dateStamp(s) : '');

  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.isError || !d) return <p className="error">{otErr(t, q.error)}</p>;
  const whole = d.nodes.some((n) => n.is_root);
  const nodeNames = d.nodes.map((n) => (n.is_root ? t('ot.whole_org_of', { org: d.from_org_name }) : n.name)).join('、');

  function done(m: string) {
    setFlow(null);
    setNotice(m);
    refresh();
  }
  async function review() {
    try {
      await treeApi.reviewDelegation(org, d!);
      done(t('ot.reviewed', { org: d!.to_org_name }));
    } catch (e) {
      setErr(otErr(t, e));
    }
  }
  async function endByGrantee() {
    if (!(await ask({ danger: true, title: t('ot.end_dlg_title', { from: d!.from_org_name }), body: t('ot.end_dlg_body'), action: t('ot.end_dlg') }))) return;
    try {
      await treeApi.endDelegation(org, d!.delegation_id);
      refresh();
      nav('..?dir=in', { relative: 'path' });
    } catch (e) {
      setErr(otErr(t, e));
    }
  }

  return (
    <section className="card" data-testid={side === 'out' ? 'dlg-detail-out' : 'dlg-detail-in'}>
      <p className="small">
        <Link to={side === 'in' ? '..?dir=in' : '..'} relative="path">
          ← {t('ot.delegations')}
        </Link>
      </p>
      <div className="toolbar wrap">
        <h2 className="section-title" style={{ margin: 0 }}>
          {side === 'out' ? t('ot.dlg_detail_out', { org: d.to_org_name }) : t('ot.dlg_detail_in', { org: d.from_org_name })}
        </h2>
        <StatusTags d={d} side={side!} />
        <span className="spacer" />
        {side === 'out' ? (
          <div className="row">
            <button className="btn btn-sm" onClick={() => setFlow({ kind: 'scope' })}>
              {t('ot.edit_scope')}
            </button>
            <button className="btn btn-sm" onClick={() => setFlow({ kind: 'term' })}>
              {t('ot.extend_term')}
            </button>
            {d.review_due && (
              <button className="btn btn-sm" onClick={() => void review()}>
                {t('ot.still_needed')}
              </button>
            )}
            <button className="btn btn-sm btn-danger-outline" onClick={() => setFlow({ kind: 'revoke' })} data-testid="dlg-revoke">
              {t('ot.revoke_dlg')}
            </button>
          </div>
        ) : (
          <div className="row">
            {(d.expandable_count ?? 0) > 0 && (
              <button className="btn btn-sm" onClick={() => setFlow({ kind: 'apply' })}>
                {t('ot.apply_ellipsis')}
              </button>
            )}
            <button className="btn btn-sm btn-primary" onClick={() => setAssign({})}>
              {t('ot.assign_members')}
            </button>
            <button className="btn btn-sm btn-danger-outline" onClick={() => void endByGrantee()}>
              {t('ot.end_dlg')}
            </button>
          </div>
        )}
      </div>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {err && <p className="error">{err}</p>}
      <table className="ot-kv">
        <tbody>
          {side === 'out' ? (
            <tr>
              <th>{t('ot.col_to_org')}</th>
              <td>
                {d.to_org_name}
                {(d.to_org_verified_kinds?.length ?? 0) > 0 && <span className="badge ot-verified">{t('ot.verified', { kinds: d.to_org_verified_kinds!.join('、') })}</span>}
                {d.to_org_code && <span className="mono small"> {t('ot.org_code', { code: d.to_org_code })}</span>}
              </td>
            </tr>
          ) : (
            <tr>
              <th>{t('ot.col_from_org')}</th>
              <td>
                {d.from_org_name}
                {d.granted_by_name && <span className="muted"> {t('ot.delegator_paren', { name: d.granted_by_name })}</span>}
              </td>
            </tr>
          )}
          <tr>
            <th>{t('ot.created_reviewed')}</th>
            <td>
              {fmt(d.created_at)}
              {d.expanded_at ? ' · ' + t('ot.expanded_at', { date: fmt(d.expanded_at) }) : ''}
              {side === 'out' && d.reviewed_at ? ' · ' + t('ot.reviewed_at', { date: fmt(d.reviewed_at) }) : ''}
              {side === 'out' && d.granted_by_name ? ' · ' + t('ot.created_by', { name: d.granted_by_name }) : ''}
            </td>
          </tr>
          <tr>
            <th>{t('ot.wz_nodes')}</th>
            <td>
              {nodeNames}
              {whole && <span className="muted small"> {t('ot.whole_org_now_later')}</span>}
            </td>
          </tr>
          <tr>
            <th>{t('ot.dlg_scope')}</th>
            <td>{actionWords(t, d.scopes)}</td>
          </tr>
          <tr>
            <th>{t('ot.term')}</th>
            <td>{d.expires_at ? t('ot.until', { date: fmt(d.expires_at) }) : t('ot.dlg_forever')}</td>
          </tr>
          <tr>
            <th>{t('ot.dlg_history')}</th>
            <td>{d.history_from ? t('ot.history_since', { date: fmt(d.history_from) }) : t('ot.dlg_history_now')}</td>
          </tr>
          <tr>
            <th>{t('ot.local_playback_full')}</th>
            <td data-testid="dlg-detail-lpf">{d.local_playback_full ? t('ot.lpf_on') : t('ot.lpf_off')}</td>
          </tr>
          <tr>
            <th>{t('ot.policy')}</th>
            <td>{d.from_mfa_required ? t('ot.policy_mfa', { from: d.from_org_name }) : t('ot.policy_none')}</td>
          </tr>
        </tbody>
      </table>

      {side === 'out' && (
        <>
          <h3 className="ot-h3">{t('ot.grantees_title', { org: d.to_org_name })}</h3>
          {(d.grantee_assignments ?? []).length === 0 ? (
            <p className="muted small">{t('ot.no_grantees')}</p>
          ) : (
            <div className="table-wrap">
              <table className="grid ot-cards" data-testid="grantees">
                <thead>
                  <tr>
                    <th>{t('ot.col_who')}</th>
                    <th>{t('ot.col_role')}</th>
                    <th>{t('ot.col_effective')}</th>
                    <th>{t('ot.col_node')}</th>
                    <th>{t('ot.col_term')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.grantee_assignments!.map((g) => (
                    <tr key={g.grantee_ref + g.node_id}>
                      <td data-label={t('ot.col_who')}>
                        {g.display_name} <span className="mono small muted">{g.grantee_ref}</span>
                      </td>
                      <td data-label={t('ot.col_role')}>
                        {t('ot.role_' + g.role, { defaultValue: g.role })}
                        {g.via_group_ref && <span className="muted small"> {t('ot.via_group', { ref: g.via_group_ref })}</span>}
                      </td>
                      <td data-label={t('ot.col_effective')}>{actionWords(t, g.effective_actions)}</td>
                      <td data-label={t('ot.col_node')}>{g.node_name ?? g.node_id}</td>
                      <td data-label={t('ot.col_term')}>{g.expires_at ? t('ot.until', { date: fmt(g.expires_at) }) : t('ot.forever')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="small">
            {t('ot.recent_ops', { n: d.recent_ops ?? 0 })} <Link to={`/audit?delegation_id=${encodeURIComponent(d.delegation_id)}`}>{t('ot.open_audit')}</Link>
          </p>
        </>
      )}

      {side === 'in' && (
        <>
          <h3 className="ot-h3">{t('ot.my_assignments_title')}</h3>
          {dAs.isLoading && <p className="muted">{t('common.loading')}</p>}
          {dAs.data && dAs.data.length === 0 && <p className="muted small">{t('ot.no_d_assignments')}</p>}
          {dAs.data && dAs.data.length > 0 && (
            <div className="table-wrap">
              <table className="grid ot-cards" data-testid="d-assignments">
                <thead>
                  <tr>
                    <th>{t('ot.col_who')}</th>
                    <th>{t('ot.col_node')}</th>
                    <th>{t('ot.col_role')}</th>
                    <th>{t('ot.col_effective')}</th>
                    <th>{t('ot.col_term')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {dAs.data.map((a) => (
                    <tr key={a.assignment_id} data-assignment={a.display_name}>
                      <td data-label={t('ot.col_who')}>
                        {a.principal_kind === 'group' ? '▦ ' : ''}
                        {a.display_name}
                        {a.principal_kind === 'group' && a.member_count !== undefined && <span className="muted small"> {t('ot.member_group_paren', { n: a.member_count })}</span>}
                      </td>
                      <td data-label={t('ot.col_node')}>{(a.node_path ?? []).join(' › ')}</td>
                      <td data-label={t('ot.col_role')}>
                        {a.role === 'custom' ? (a.role_name ?? t('ot.role_custom')) : t('ot.role_' + a.role)}
                        {(a.extra_actions?.length ?? 0) > 0 && <span className="muted small"> + {actionWords(t, a.extra_actions!)}</span>}
                      </td>
                      <td data-label={t('ot.col_effective')}>
                        {actionWords(t, a.effective_actions ?? [])}
                        {(a.ignored_actions?.length ?? 0) > 0 && <div className="muted small">{t('ot.not_effective', { what: actionWords(t, a.ignored_actions!) })}</div>}
                        {(a.out_of_scope?.length ?? 0) > 0 && <div className="muted small">{t('ot.out_of_scope', { what: actionWords(t, a.out_of_scope!) })}</div>}
                        {(a.applicable?.length ?? 0) > 0 && <span className="badge badge-warn">{t('ot.can_expand')}</span>}
                      </td>
                      <td data-label={t('ot.col_term')}>{termText(t, a, i18n.language)}</td>
                      <td>
                        <div className="row">
                          <button className="btn btn-sm" onClick={() => setAssign({ existing: a })}>
                            {t('common.edit')}
                          </button>
                          <button className="btn btn-sm btn-danger-outline" onClick={() => setFlow({ kind: 'd-revoke', a })}>
                            {t('common.action_revoke')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted small">{t('ot.delegator_sees_list', { from: d.from_org_name })}</p>
        </>
      )}

      {flow?.kind === 'revoke' && (
        <PreviewDialog
          open
          title={t('ot.revoke_dlg_title', { org: d.to_org_name })}
          target={nodeNames}
          load={() => treeApi.previewDelegation(org, d.delegation_id, { end: true })}
          submit={(p) => treeApi.endDelegation(org, d.delegation_id, { preview_token: p.token, reason: p.reason })}
          action={t('ot.revoke_dlg')}
          reasonOptional
          losingTitle={(n) => t('ot.will_lose', { org: d.to_org_name, n: n.people })}
          note={t('ot.revoke_dlg_note', { org: d.to_org_name })}
          onDone={() => done(t('ot.dlg_revoked', { org: d.to_org_name }))}
          onClose={() => setFlow(null)}
        />
      )}
      {(flow?.kind === 'scope' || flow?.kind === 'term') && <EditDelegation org={org} d={d} mode={flow.kind} close={() => setFlow(null)} done={done} />}
      {flow?.kind === 'apply' && <ApplyDialog org={org} d={d} close={() => setFlow(null)} done={done} />}
      {flow?.kind === 'd-revoke' && <DRevokeDialog org={org} d={d} a={flow.a} close={() => setFlow(null)} done={done} />}
      <AssignDialog org={org} target={assign ? { kind: 'delegation', delegation: d } : null} existing={assign?.existing} close={() => setAssign(null)} done={() => refresh()} />
      {dialog}
    </section>
  );
}

/** 〔編輯範圍〕/〔延長 / 設期限〕：先選，再按〔下一步〕打預覽（縮小列失去的人；擴大成高風險要核可）。 */
function EditDelegation({ org, d, mode, close, done }: { org: MyOrg; d: Delegation; mode: 'scope' | 'term'; close(): void; done(m: string): void }) {
  const { t } = useTranslation();
  const [scopes, setScopes] = useState<string[]>(d.scopes);
  const [until, setUntil] = useState(d.expires_at ? d.expires_at.slice(0, 10) : '');
  const [preview, setPreview] = useState(false);
  const body = mode === 'scope' ? { scopes } : { expires_at: until ? new Date(until + 'T23:59:59').toISOString() : null };
  if (preview)
    return (
      <PreviewDialog
        open
        title={mode === 'scope' ? t('ot.edit_scope_title', { org: d.to_org_name }) : t('ot.extend_term_title', { org: d.to_org_name })}
        target={d.to_org_name}
        load={() => treeApi.previewDelegation(org, d.delegation_id, body)}
        submit={(p) => treeApi.updateDelegation(org, d, { ...body, preview_token: p.token, reason: p.reason })}
        action={mode === 'scope' ? t('ot.save_scope') : t('ot.save_term')}
        reasonOptional
        losingTitle={(n) => t('ot.will_lose', { org: d.to_org_name, n: n.people })}
        onDone={() => done(t('ot.dlg_updated'))}
        onClose={close}
      />
    );
  return (
    <Dialog open title={mode === 'scope' ? t('ot.edit_scope_title', { org: d.to_org_name }) : t('ot.extend_term_title', { org: d.to_org_name })} onClose={close}>
      <div className="form">
        {mode === 'scope' ? (
          <div className="ot-scope-grid">
            {DELEGATION_SCOPES.map((s) => (
              <label key={s} className="row-inline">
                <input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
                {t('ot.scope_' + s.replace(/\./g, '_'))}
                {HIGH_RISK_SCOPES.includes(s) && <span className="badge badge-warn">{t('ot.high_risk_badge')}</span>}
              </label>
            ))}
          </div>
        ) : (
          <>
            <select className="input" value={until ? 'date' : 'forever'} onChange={(e) => setUntil(e.target.value === 'date' ? new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10) : '')} aria-label={t('ot.term')}>
              <option value="forever">{t('ot.dlg_forever')}</option>
              <option value="date">{t('ot.dlg_until')}</option>
            </select>
            {until && <input className="input" type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label={t('ot.dlg_until')} />}
          </>
        )}
        <p className="muted small">{t('ot.edit_dlg_hint')}</p>
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={mode === 'scope' && scopes.length === 0} onClick={() => setPreview(true)}>
            {t('ot.next_preview')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** 〔套用…〕（o28）：A 擴大範圍後，每筆指派「目前生效 / 套用後多出」，逐筆勾 → step-up。 */
export function ApplyDialog({ org, d, close, done }: { org: MyOrg; d: Delegation; close(): void; done(m: string): void }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['ot', org.org_id, 'dlg-assignments', d.delegation_id], queryFn: () => treeApi.dAssignments(org, d.delegation_id) });
  const rows = (q.data ?? []).filter((a) => (a.applicable?.length ?? 0) > 0);
  const [sel, setSel] = useState<Set<string> | null>(null);
  const chosen = sel ?? new Set(rows.map((a) => a.assignment_id));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    setErr(null);
    try {
      const r = await treeApi.dApply(org, d.delegation_id, { assignment_ids: [...chosen], expected_version: d.version });
      done(t('ot.applied', { n: r?.applied ?? chosen.size }));
      close();
    } catch (e) {
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('ot.apply_title', { from: d.from_org_name })} onClose={close} wide>
      <div className="form" data-testid="apply-dialog">
        <p className="muted small">{t('ot.apply_hint', { from: d.from_org_name })}</p>
        {q.isLoading && <p className="muted">{t('common.loading')}</p>}
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th />
                <th>{t('ot.col_who')}</th>
                <th>{t('ot.col_effective_now')}</th>
                <th>{t('ot.col_after_apply')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.assignment_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={chosen.has(a.assignment_id)}
                      aria-label={a.display_name}
                      onChange={(e) => {
                        const n = new Set(chosen);
                        if (e.target.checked) n.add(a.assignment_id);
                        else n.delete(a.assignment_id);
                        setSel(n);
                      }}
                    />
                  </td>
                  <td>{a.display_name}</td>
                  <td>{actionWords(t, a.effective_actions ?? [])}</td>
                  <td>+ {actionWords(t, a.applicable ?? [])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || chosen.size === 0} onClick={() => void go()}>
            {t('ot.apply_n', { n: chosen.size })}
            {t('ot.reauth_suffix')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** 撤銷 D 自己成員在委派子樹的指派（o29b）：列失去的動作與範圍、此刻在串流的人數；撤 D 的節點管理員時列他授出的指派可一併撤銷。 */
function DRevokeDialog({ org, d, a, close, done }: { org: MyOrg; d: Delegation; a: Assignment; close(): void; done(m: string): void }) {
  const { t } = useTranslation();
  const grants = useQuery({
    queryKey: ['ot', org.org_id, 'dlg-granted-by', d.delegation_id, a.principal_id],
    queryFn: () => treeApi.dAssignments(org, d.delegation_id, { granted_by: a.principal_id }),
    enabled: a.role === 'node_admin' && a.principal_kind === 'user',
  });
  const [also, setAlso] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const where = [d.from_org_name, ...(a.node_path ?? [])].join(' › ');
  async function go() {
    setBusy(true);
    setErr(null);
    try {
      await treeApi.dRevoke(org, d.delegation_id, a, { ...(reason.trim() ? { reason: reason.trim() } : {}), ...(also.size ? { also_revoke: [...also] } : {}) });
      done(t('ot.revoked'));
      close();
    } catch (e) {
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('ot.d_revoke_title', { who: a.display_name, where })} onClose={close}>
      <div className="form" data-testid="d-revoke">
        <p className="small">{t('ot.d_revoke_lose', { what: actionWords(t, a.effective_actions ?? a.actions) })}</p>
        <p className="small">{t('ot.d_revoke_streams', { n: a.active_streams ?? 0 })}</p>
        <p className="small">{t('ot.d_revoke_notify')}</p>
        {(grants.data ?? []).length > 0 && (
          <fieldset className="field">
            <legend>{t('ot.granted_by_target_title')}</legend>
            {grants.data!.map((g) => (
              <label key={g.assignment_id} className="row-inline">
                <input
                  type="checkbox"
                  checked={also.has(g.assignment_id)}
                  onChange={(e) => {
                    const n = new Set(also);
                    if (e.target.checked) n.add(g.assignment_id);
                    else n.delete(g.assignment_id);
                    setAlso(n);
                  }}
                />
                {g.display_name} · {t('ot.role_' + g.role, { defaultValue: g.role })} · {t('ot.also_revoke')}
              </label>
            ))}
          </fieldset>
        )}
        <p className="ot-warn small">{t('ot.d_revoke_yellow', { from: d.from_org_name })}</p>
        <label className="field">
          <span>
            {t('ot.reason')} {t('ot.optional')}
          </span>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
        </label>
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void go()} data-testid="d-revoke-submit">
            {t('ot.revoke_assign')}
            {t('ot.reauth_suffix')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
